/**
 * Persistence for column widths the user set by dragging.
 *
 * Its own key rather than the shared `cowbell` uiSettings blob, because that
 * blob cannot answer "did the user resize this?": `useUiSettingsAutosave`
 * writes `columnWidths` on every render, only ~37 of ~50 listings include it,
 * and each caller serialises a stale whole-store snapshot so concurrent writers
 * drop each other's keys. Writing only on a real drag keeps the signal honest
 * for every listing without touching any of them.
 */
const STORAGE_KEY = 'cowbellTableColumnWidths';

type ColumnWidth = { columnName: string; width: number | string };

const readStore = (): Record<string, ColumnWidth[]> => {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') || {};
  } catch {
    // Corrupt or unreadable (private mode, quota) — fall back to defaults.
    return {};
  }
};

/** Widths the user previously dragged on this table, or `[]` if none. */
export const readColumnWidths = (tableId: string | undefined): ColumnWidth[] => {
  if (!tableId) {
    return [];
  }
  const saved = readStore()[tableId];
  return Array.isArray(saved) ? saved : [];
};

/** Records a user-dragged layout. No-op when there is no table id to key it by. */
export const writeColumnWidths = (
  tableId: string | undefined,
  widths: ColumnWidth[]
): void => {
  if (!tableId || !Array.isArray(widths)) {
    return;
  }
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readStore(), [tableId]: widths })
    );
  } catch {
    // Storage failures must never break resizing.
  }
};

/**
 * Applies saved widths onto the table's current width entries, or `null` when
 * no value changes. Purely a value merge — `getRestoredColumnWidths` decides
 * ownership, since identical values still mean the user resized this table.
 *
 * Maps over `current` rather than concatenating so every configured column
 * keeps an entry: DevExtreme's `specifyWidths` throws (`COLUMN_RESIZING_ERROR`)
 * if a data column has no width, which a saved layout predating a new column
 * would cause. Columns that no longer exist are dropped for the same reason.
 */
export const mergeSavedColumnWidths = (
  saved: ColumnWidth[],
  current: ColumnWidth[]
): ColumnWidth[] | null => {
  if (!saved?.length || !current?.length) {
    return null;
  }

  const savedByName = new Map(saved.map((s) => [s.columnName, s.width]));
  let changed = false;

  const merged = current.map((entry) => {
    if (!savedByName.has(entry.columnName)) {
      return entry;
    }
    const width = savedByName.get(entry.columnName);
    if (width === entry.width) {
      return entry;
    }
    changed = true;
    return { ...entry, width } as ColumnWidth;
  });

  return changed ? merged : null;
};
